import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Target, PencilSimple } from '@phosphor-icons/react';

import type { CoachGoalView } from '@/api/coach';
import { GoalDialog } from '@/components/coach/GoalDialog';
import { askCoach } from '@/components/coach/cards';

// GoalStrip — the season goals slimmed to a single compact, horizontally
// scrollable line (was a large wrapping chip block). Each goal is a small pill:
// a status dot, its label, and its headline benchmark number if measured.
// Tapping a goal asks the AI how to improve it; the pencil opens the goal
// editor in a dialog (so the page content doesn't shift).
export function GoalStrip({
  slug,
  goals,
  hasGoals,
}: {
  slug: string;
  goals: CoachGoalView[];
  hasGoals: boolean;
}) {
  const { t } = useTranslation('common');
  const [editorOpen, setEditorOpen] = useState(false);

  const goalTitle = (key: string) =>
    key ? t(`coach.goals.catalog.${key}.title`) : t('coach.dashboard.general');

  return (
    <>
      {!hasGoals ? (
        <button
          type="button"
          onClick={() => setEditorOpen(true)}
          className="inline-flex min-h-11 w-fit items-center gap-2 rounded-pill border border-dashed border-border px-4 py-2 text-sm font-semibold text-muted-foreground transition hover:scale-[1.04] hover:text-foreground motion-reduce:hover:scale-100"
        >
          <Target size={16} weight="bold" aria-hidden />
          {t('coach.dashboard.setGoals')}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <ul className="flex flex-1 items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {goals.map((g) => (
              <GoalPill key={g.goalKey} goal={g} label={goalTitle(g.goalKey)} />
            ))}
          </ul>
          <button
            type="button"
            aria-label={t('coach.dashboard.editGoals')}
            title={t('coach.dashboard.editGoals')}
            onClick={() => setEditorOpen(true)}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:scale-[1.08] hover:text-foreground motion-reduce:hover:scale-100"
          >
            <PencilSimple size={16} weight="bold" aria-hidden />
          </button>
        </div>
      )}
      <GoalDialog open={editorOpen} onClose={() => setEditorOpen(false)} slug={slug} />
    </>
  );
}

function GoalPill({ goal, label }: { goal: CoachGoalView; label: string }) {
  const { t } = useTranslation('common');
  const worsened = goal.status === 'attention' && goal.signalCount === 0;
  // Dot colour by honest status: red for risk/worsening, muted for not-measured,
  // lime for on-track, amber for attention with open signals.
  const dot =
    goal.status === 'risk' || worsened
      ? 'bg-destructive'
      : goal.status === 'not_measured'
        ? 'bg-foreground/30'
        : goal.status === 'on_track'
          ? 'bg-primary'
          : 'bg-orange';

  const b = goal.benchmark;
  const headline =
    b && b.current != null
      ? b.unit === 'percent'
        ? `${Math.round(b.current)}%`
        : `${Math.round(b.current)}`
      : null;

  return (
    <li className="shrink-0">
      <button
        type="button"
        aria-label={t('coach.dashboard.howTo', { goal: label })}
        title={t('coach.dashboard.howTo', { goal: label })}
        onClick={() => askCoach(t('coach.dashboard.howToPrompt', { goal: label }))}
        className="inline-flex min-h-9 items-center gap-2 rounded-pill border border-border bg-card px-3 py-1.5 text-sm transition hover:scale-[1.04] hover:border-foreground/30 motion-reduce:hover:scale-100"
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} aria-hidden />
        <span className="whitespace-nowrap font-semibold">{label}</span>
        {headline && <span className="tabular-nums text-muted-foreground">{headline}</span>}
      </button>
    </li>
  );
}
